import axiosInstance from './axiosInstance'

export interface ContactActivity {
  id:           string
  contactId:    string
  type:         string
  description:  string
  metadataJson: string | null
  performedBy:  AuditUser
  createdAt:    string
}

export interface AuditUser {
  id: string
  name: string
  email: string
  avatarUrl: string | null
}

export interface ContactCustomer {
  id: string
  name: string
  phone: string
  companyName: string | null
  assignedTo: AuditUser | null
  orgName: string | null
  warehouseName: string | null
}

export interface Contact {
  id: string
  name: string
  phone: string
  email: string | null
  role: string | null
  description: string | null
  customerId: string
  customer: ContactCustomer
  createdBy: AuditUser | null
  tags: TagMini[]
  createdAt: string
  updatedAt: string
  orgName?: string | null
  lobName?: string | null
  warehouseName?: string | null
}

export interface TagMini {
  id: string
  name: string
  color: string
}

export interface PaginatedContacts {
  items: Contact[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface ContactFilters {
  search?: string
  customerId?: string
  orgIds?: string[]
  lobIds?: string[]
  warehouseIds?: string[]
  fromDate?: string
  toDate?: string
  ownerId?: string
  page?: number
  limit?: number
  pageSize?: number
}

export interface CreateContactPayload {
  name: string
  phone: string
  email?: string
  role?: string
  customerId: string
  orgId?: string
  lobId?: string
  warehouseId?: string
}

export interface UpdateContactPayload {
  name?: string
  phone?: string
  email?: string | null
  role?: string | null
  description?: string | null
}

export const contactsApi = {
  export: async (filters: ContactFilters = {}): Promise<void> => {
    const params = {
      ...filters,
      pageSize: filters.pageSize ?? filters.limit,
    }
    const res = await axiosInstance.get('/contacts/export', { params, responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([res.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'Contacts.xlsx')
    document.body.appendChild(link)
    link.click()
    link.remove()
  },

  getAll: async (filters: ContactFilters = {}): Promise<PaginatedContacts> => {
    const params = {
      ...filters,
      pageSize: filters.pageSize ?? filters.limit,
    }
    const res = await axiosInstance.get<{ data: PaginatedContacts }>('/contacts', { params })
    return res.data.data
  },

  getById: async (id: string): Promise<Contact> => {
    const res = await axiosInstance.get<{ data: Contact }>('/contacts/' + id)
    return res.data.data
  },

  create: async (payload: CreateContactPayload): Promise<Contact> => {
    const res = await axiosInstance.post<{ data: Contact }>('/contacts', payload)
    return res.data.data
  },

  update: async (id: string, payload: UpdateContactPayload): Promise<Contact> => {
    const res = await axiosInstance.put<{ data: Contact }>('/contacts/' + id, payload)
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete('/contacts/' + id)
  },

  search: async (query: string): Promise<Contact[]> => {
    const res = await axiosInstance.get<{ data: PaginatedContacts }>('/contacts', {
      params: { search: query, page: 1, pageSize: 20 },
    })
    return res.data.data.items
  },

  getActivity: async (contactId: string): Promise<ContactActivity[]> => {
    const res = await axiosInstance.get<{ data: ContactActivity[] }>(`/contacts/${contactId}/activities`)
    return res.data.data
  },

  addActivity: async (contactId: string, payload: any): Promise<ContactActivity> => {
    const res = await axiosInstance.post<{ data: ContactActivity }>(`/contacts/${contactId}/activities`, payload)
    return res.data.data
  },

  addTag: async (id: string, tagId: string): Promise<Contact> => {
    const res = await axiosInstance.post<{ data: Contact }>(`/contacts/${id}/tags/${tagId}`)
    return res.data.data
  },

  removeTag: async (id: string, tagId: string): Promise<Contact> => {
    const res = await axiosInstance.delete<{ data: Contact }>(`/contacts/${id}/tags/${tagId}`)
    return res.data.data
  },
}
